import React, { useState, useRef, useEffect } from 'react';
import { translations } from '../../translations';

interface FollowerGainsChartProps {
  groupedHistory: Array<{
    date: string;
    count: number;
    gains: number;
  }>;
  isDarkMode: boolean;
  language: string;
}

export function FollowerGainsChart({ groupedHistory, isDarkMode, language }: FollowerGainsChartProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Calculate daily gains
  const dailyGains = React.useMemo(() => {
    return groupedHistory.map((entry, index) => {
      if (index === 0) return { ...entry, dailyGain: 0 };
      
      const previousEntry = groupedHistory[index - 1];
      const dailyGain = entry.count - previousEntry.count;
      
      return {
        ...entry,
        dailyGain
      };
    });
  }, [groupedHistory]);

  const formatNumber = (num: number) => {
    const prefix = num >= 0 ? '+' : '';
    if (Math.abs(num) >= 1000000) {
      return prefix + (num / 1000000).toFixed(1).replace(/\.0$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + 'M';
    }
    if (Math.abs(num) >= 1000) {
      return prefix + (num / 1000).toFixed(1).replace(/\.0$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + 'K';
    }
    return prefix + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(language === 'FR' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  };

  const { minGains, maxGains, gainsLabels, zeroY } = React.useMemo(() => {
    if (!dailyGains.length) {
      return { minGains: -100, maxGains: 100, gainsLabels: [-100, -50, 0, 50, 100], zeroY: 50 };
    }

    const gains = dailyGains.map(entry => entry.dailyGain);
    const min = Math.min(0, ...gains); // Ensure we include 0 or lower values
    const max = Math.max(0, ...gains); // Ensure we include 0 or higher values
    
    // Ensure we have both positive and negative space with some padding
    const absMax = Math.max(Math.abs(min), Math.abs(max)) * 1.2;
    const adjustedMin = -absMax;
    const adjustedMax = absMax;
    
    const range = adjustedMax - adjustedMin;
    const step = Math.ceil(range / 4);
    
    // Create symmetric labels around zero
    const labels = Array.from({ length: 5 }, (_, i) => 
      Math.round(adjustedMin + (step * i))
    );
    
    // Calculate zero position as percentage
    const zeroY = ((adjustedMax) / (adjustedMax - adjustedMin)) * 100;
    
    return { 
      minGains: adjustedMin, 
      maxGains: adjustedMax, 
      gainsLabels: labels,
      zeroY 
    };
  }, [dailyGains]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dailyGains.length || !dimensions.width) return;

    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    const barWidth = dimensions.width / dailyGains.length;
    const index = Math.floor(x / barWidth);
    
    if (index >= 0 && index < dailyGains.length) {
      const entry = dailyGains[index];
      const xPos = index * barWidth + barWidth / 2;
      
      // Position tooltip above bar for positive values, below for negative
      const yOffset = entry.dailyGain >= 0 ? -40 : 20;
      const yPos = dimensions.height * (zeroY / 100) - ((entry.dailyGain / (maxGains - minGains)) * dimensions.height) + yOffset;

      setTooltip({
        x: xPos,
        y: yPos,
        content: `${formatDate(entry.date)}: ${formatNumber(entry.dailyGain)}`
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  if (!dailyGains.length) {
    return (
      <div className="h-[200px] flex items-center justify-center">
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t.no_data_available}
        </p>
      </div>
    );
  }

  return (
    <div className="flex">
      <div className="pr-4 flex flex-col justify-between text-[10px]">
        {[...gainsLabels].reverse().map((value, index) => (
          <div
            key={index}
            className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}
          >
            {formatNumber(value)}
          </div>
        ))}
      </div>

      <div className="flex-1 min-w-0">
        <div className="relative h-[200px]">
          <div className="absolute inset-0">
            {/* Grid lines */}
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={`h-${i}`}
                className={`absolute w-full border-t ${
                  isDarkMode ? 'border-gray-800' : 'border-gray-100'
                }`}
                style={{ top: `${(i * 25)}%` }}
              />
            ))}
            
            {/* Zero line - make it more prominent */}
            <div 
              className={`absolute w-full border-t-2 ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}
              style={{ top: `${zeroY}%` }}
            />
          </div>

          <svg
            ref={svgRef}
            className="absolute inset-0 w-full h-full overflow-visible"
            preserveAspectRatio="none"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {dailyGains.map((entry, i) => {
              const barWidth = 100 / dailyGains.length;
              const x = i * barWidth;
              
              // Calculate height and position relative to zero line
              const heightPercent = Math.abs((entry.dailyGain / (maxGains - minGains)) * 100);
              const y = entry.dailyGain >= 0 
                ? zeroY - heightPercent 
                : zeroY;

              return (
                <rect
                  key={i}
                  x={`${x}%`}
                  y={`${y}%`}
                  width={`${barWidth * 0.8}%`}
                  height={`${heightPercent}%`}
                  fill={entry.dailyGain >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'}
                  rx="2"
                  transform={`translate(${barWidth * 0.1}%, 0)`}
                  className="transition-all duration-200 hover:opacity-80"
                />
              );
            })}

            {tooltip && (
              <foreignObject
                x={tooltip.x - 60}
                y={tooltip.y - 20}
                width="120"
                height="40"
                style={{ overflow: 'visible' }}
              >
                <div className={`px-2 py-1 text-xs rounded shadow-lg text-center ${
                  isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-200 border border-[#3b3b3b]'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}>
                  {tooltip.content}
                </div>
              </foreignObject>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}